import { getPlacementValidations, hasOnlyPlacement, hasOnlyBoundaryPlacements } from "~/utils/self-assessment-placement";
import {
  SelectionType,
  SelfAssessmentStep,
  WordSelectionMode,
} from "~/classes/models/self-assessment/self-assessment-step.model";
import { useParagraphSentences } from "../quill-selection/useParagraphSentences";
import { useRelevantInstanceSentence } from "../quill-selection/useRelevantinstanceSentence";
import { useDuplicateTokenInstanceValidation } from "./useDuplicateTokenInstanceValidation";
import { useSelfAssessmentValidateInstance } from "./useSelAssessmentValidateInstance";

export const useSelfAssessmentStepValidation = ({
  documentId,
  step,
  relevantInstances,
  quill,
}: {
  documentId: string;
  step: SelfAssessmentStep;
  relevantInstances: any[];
  quill: any;
}) => {
  const documentSelfAssessmentStore =
    useDocumentSelfAssessmentStore(documentId);
  const {
    stepExpectedInstances,
    bodyParagraphs,
    bodyBlocks,
    stepParagraphPositions,
    quillDelta,
    headerParagraphs,
  } = storeToRefs(documentSelfAssessmentStore);

  if (
    step.selectionType == SelectionType.token &&
    step.wordSelectionMode != WordSelectionMode.multipleWords
  ) {
    useDuplicateTokenInstanceValidation(
      quill,
      step.criteriaAttribute.attribute,
      relevantInstances
    );
  }

  const paragraphPositions = stepParagraphPositions.value(step);
  let firstInstanceError: unknown;
  for (const relevantInstance of relevantInstances) {
    try {
      useSelfAssessmentValidateInstance(
        step,
        relevantInstance,
        bodyParagraphs.value,
        paragraphPositions,
        quillDelta.value.ops,
        quill
      );
    } catch (error) {
      firstInstanceError ??= error;
    }
  }
  // Count failures must not bypass feedback on selections already present.
  if (stepExpectedInstances.value(step) > 0) {
    if (
      hasOnlyBoundaryPlacements(step)
    ) {
      if (relevantInstances.length < stepExpectedInstances.value(step)) {
        throw new Error("not-enough-instances");
      }
    } else {
      if (relevantInstances.length < stepExpectedInstances.value(step)) {
        throw new Error("not-enough-instances");
      }

      if (step.rquireExactInstancesCount == true) {
        if (step.evaluationMethod == "paragraph") {
          if (relevantInstances.length != stepExpectedInstances.value(step)) {
            throw new Error("too-many-instances-error");
          }
        }
      }
    }
  }

  // Visit every instance so valid selections clear and all invalid ones light up.
  if (firstInstanceError) throw firstInstanceError;

  const relevantInstanceParagraphIndexes = relevantInstances
    .map((instance) => {
      return instance.paragraphIndex;
    })
    .filter((index) => index != undefined);

  const uniqueParagraphIndexes = [...new Set(relevantInstanceParagraphIndexes)];
  const paragraphSentenceRanges = uniqueParagraphIndexes.map(
    (paragraphIndex) => {
      return {
        index: paragraphIndex,
        sentenceRanges: useParagraphSentences(
          quillDelta.value.ops,
          quill,
          paragraphIndex
        ),
      };
    }
  );

  if (getPlacementValidations(step).length > 0) {
    if (
      hasOnlyPlacement(step, "bodyParagraphs") &&
      stepExpectedInstances.value(step) > 0
    ) {
      const paragraphDistribution = relevantInstances.map((instance) => {
        return instance.paragraphIndex;
      });

      const uniqueParagraphs = [...new Set(paragraphDistribution)];

      // A stray header selection must not override independently detected essay
      // content. Require both structural evidence and a checklist body position.
      const confirmedBodyIndexes = new Set(
        bodyBlocks.value
          .filter((block) => bodyParagraphs.value.some((paragraph) => paragraph.index === block.sourceIndex))
          .map((block) => block.sourceIndex)
      );
      const hasInvalidParagraphs = headerParagraphs.value.filter((index) => {
        return uniqueParagraphs.includes(index) && !confirmedBodyIndexes.has(index);
      });

      if (hasInvalidParagraphs.length > 0) {
        throw new Error("placement-error");
      }

      if (uniqueParagraphs.length < stepExpectedInstances.value(step)) {
        throw new Error("distribution-error");
      }
    }
  }

  if (hasOnlyPlacement(step, "topicClinchers")) {
    const relevantParagraphIndexes = stepParagraphPositions
      .value(step)
      .map((position) => {
        return bodyParagraphs.value[position - 1];
      })
      .filter((paragraph) => paragraph != undefined)
      .map((paragraph) => paragraph.index);

    const uniqueRelevantParagraphIndexes = [
      ...new Set(relevantParagraphIndexes),
    ];

    for (const paragraphIndex of uniqueRelevantParagraphIndexes) {
      const paragraphInstances = relevantInstances.filter((instance) => {
        return instance.paragraphIndex == paragraphIndex;
      });

      const paragraphSentences = useParagraphSentences(
        quillDelta.value.ops,
        quill,
        paragraphIndex,
        { checkItalic: true }
      );

      if (paragraphSentences == undefined || paragraphSentences.length == 0) {
        throw new Error("placement-error");
      }

      let firstReviewableSentenceIndex = 0;
      let lastReviewableSentenceIndex = paragraphSentences.length - 1;

      if (
        paragraphSentences[firstReviewableSentenceIndex].isItalic &&
        paragraphSentences[lastReviewableSentenceIndex].isItalic
      ) {
        firstReviewableSentenceIndex = 1;
        lastReviewableSentenceIndex = paragraphSentences.length - 2;
      }

      let firstSentenceInstancesCount = 0;
      let lastSentenceInstancesCount = 0;

      for (const paragraphInstance of paragraphInstances) {
        const instanceSentence = useRelevantInstanceSentence(
          paragraphSentences,
          paragraphInstance
        );

        if (instanceSentence == undefined) {
          throw new Error("placement-error");
        }

        if (instanceSentence.sentenceIndex == firstReviewableSentenceIndex) {
          firstSentenceInstancesCount += 1;
        }

        if (instanceSentence.sentenceIndex == lastReviewableSentenceIndex) {
          lastSentenceInstancesCount += 1;
        }
      }

      if (firstSentenceInstancesCount < 2 || lastSentenceInstancesCount < 2) {
        throw new Error("distribution-error");
      }
    }
  }

  step.assessmentResults = true;

  return true;
};
